import {isFeatureEnabled} from '@github-ui/feature-flags'

/**
 * IMPORTANT NOTICE: Feature flags added here must also be added to the `packages/feature-flags/client-feature-flags.ts` file
 * or they will not be available to check in this file!
 */

class CopilotFeatureFlags {
  get immersiveFigmaIntegration() {
    return isFeatureEnabled('copilot_immersive_figma_integration')
  }

  get focusTextAreaInsteadOfWorkbench() {
    return isFeatureEnabled('copilot_focus_text_area_instead_of_workbench')
  }

  get forceLegacyChatDefaultModel() {
    return isFeatureEnabled('copilot_api_force_legacy_base_chat_model')
  }

  get domPageContext() {
    return isFeatureEnabled('copilot_use_dom_page_context')
  }

  /**
   * When enabled, the interview survey dialog replaces the 'Give feedback' dialog in copilot chat.
   * @returns {boolean} Whether the feature is enabled.
   */
  get copilotChatInterviewSurvey() {
    return isFeatureEnabled('copilot_chat_interview_survey')
  }

  get attachMultipleImages() {
    return isFeatureEnabled('copilot_chat_attach_multiple_images')
  }

  get compactTables() {
    return isFeatureEnabled('copilot_chat_compact_tables')
  }

  get ahpCustomInstructions() {
    return isFeatureEnabled('copilot_chat_custom_instructions_ahp')
  }

  get clearModelSelectionForDefaultChange() {
    return isFeatureEnabled('copilot_chat_clear_model_selection_for_default_change')
  }

  get enableToolCallLogs() {
    return isFeatureEnabled('copilot_chat_enable_tool_call_logs')
  }

  /**
   * When enabled, switching the model or reasoning effort in Copilot Chat surfaces an inline
   * timeline notice (e.g. "Model changed to …" / "Reasoning effort changed from … to …"),
   * matching the Copilot Desktop app.
   * @returns {boolean} Whether the feature is enabled.
   */
  get modelChangeNotice() {
    return isFeatureEnabled('copilot_chat_model_change_notice')
  }

  get minimizeContextualChat() {
    return isFeatureEnabled('copilot_chat_minimize_contextual')
  }

  /**
   * When enabled, copilot textarea inputs size themselves purely in CSS (grid-stacking +
   * `field-sizing`) instead of the legacy per-keystroke JS autosize. Shared across the chat input and
   * the Mission Control composer/steering inputs; gated so the layout rewrite can roll out safely with
   * a fast fallback to the JS path.
   * @returns {boolean} Whether the feature is enabled.
   */
  get cssTextareaAutosize() {
    return isFeatureEnabled('copilot_css_textarea_autosize')
  }

  get interspersedToolCalls() {
    return isFeatureEnabled('copilot_chat_interspersed_tool_calls')
  }

  get customCopilots() {
    return isFeatureEnabled('copilot_custom_copilots')
  }

  get maxUpsellEnabled() {
    return isFeatureEnabled('copilot_chat_max_upsell')
  }

  get customCopilotsFeaturePreview() {
    return isFeatureEnabled('copilot_custom_copilots_feature_preview')
  }

  get workbenchPlugin() {
    return isFeatureEnabled('copilot_workbench')
  }

  get copilotChatOpeningThreadSwitch() {
    return isFeatureEnabled('copilot_chat_opening_thread_switch')
  }

  get copilotDuplicateThread() {
    return isFeatureEnabled('copilot_duplicate_thread')
  }

  get copilotShareActiveSubthread() {
    return isFeatureEnabled('copilot_share_active_subthread')
  }

  get copilotChatO1Tools() {
    return isFeatureEnabled('copilot_api_tools_for_non_streaming_models')
  }

  get showDeepCodeSearchButton() {
    return (
      isFeatureEnabled('copilot_show_deep_code_search_button') && isFeatureEnabled('copilot_api_search_agent_skill')
    )
  }

  get deleteAllConversations() {
    return isFeatureEnabled('copilot_delete_all_conversations')
  }

  get staffPromptDialog() {
    return isFeatureEnabled('copilot_staff_prompt_dialog')
  }

  get staffToolsMenu() {
    return isFeatureEnabled('copilot_staff_tools_menu')
  }

  get visionAllowedInClaude() {
    return isFeatureEnabled('copilot_chat_vision_in_claude')
  }

  get premiumRequestQuotasEnabled() {
    return isFeatureEnabled('copilot_premium_request_quotas')
  }

  get prominentUpgradeButton() {
    return isFeatureEnabled('copilot_prominent_upgrade_button')
  }

  get freeToPaidTelemetry() {
    return isFeatureEnabled('copilot_free_to_paid_telem')
  }

  get freeToPaidSettingsUpgrade() {
    return isFeatureEnabled('copilot_ftp_settings_upgrade')
  }

  get freeToPaidYourCopilotSettings() {
    return isFeatureEnabled('copilot_ftp_your_copilot_settings')
  }

  get freeToPaidUpgradeToProFromModels() {
    return isFeatureEnabled('copilot_ftp_upgrade_to_pro_from_models')
  }

  get globalMenuFullPageChatLabel() {
    return isFeatureEnabled('copilot_global_menu_full_page_chat_label')
  }

  get spacesImagesEnabled() {
    return isFeatureEnabled('copilot_custom_copilots_images')
  }

  get immersiveJobResultPreview() {
    return isFeatureEnabled('copilot_immersive_job_result_preview')
  }

  get disableModelPickerWhileStreaming() {
    return isFeatureEnabled('copilot_chat_disable_model_picker_while_streaming')
  }

  get freeToPaidHyperspaceUpgradePrompt() {
    return isFeatureEnabled('copilot_ftp_hyperspace_upgrade_prompt')
  }

  get reduceChatQuotaChecks() {
    return isFeatureEnabled('copilot_chat_reduce_quota_checks')
  }

  get requestTracing() {
    return isFeatureEnabled('copilot_api_staff_only_tracing')
  }

  get categoryRateLimitMessages() {
    return isFeatureEnabled('copilot_chat_category_rate_limit_messages')
  }

  get fileBlockRefMatching() {
    return isFeatureEnabled('copilot_file_block_ref_matching')
  }

  get immersiveEmbeddedDraggable() {
    return isFeatureEnabled('copilot_immersive_embedded_draggable')
  }

  get immersiveEmbeddedImplicitReferences() {
    return isFeatureEnabled('copilot_immersive_embedded_implicit_references')
  }

  get immersiveEmbeddedSkipCopilotApiTokenForDotcomContext() {
    return isFeatureEnabled('copilot_immersive_embedded_skip_copilot_api_token_for_dotcom_context')
  }

  get copilotResourcePanel() {
    return isFeatureEnabled('copilot_resource_panel')
  }

  get quickChat() {
    return isFeatureEnabled('copilot_quick_chat')
  }

  get spacesIndividualPoliciesEnabled() {
    return isFeatureEnabled('copilot_spaces_individual_policies_ga')
  }

  get sweAgentUseSubagents() {
    return isFeatureEnabled('copilot_swe_agent_use_subagents')
  }

  get chatInputCommands() {
    return isFeatureEnabled('copilot_chat_input_commands')
  }

  get removeAssistiveChatButton() {
    return isFeatureEnabled('copilot_chat_remove_assistive_button')
  }

  get taskWithinChatThread() {
    return isFeatureEnabled('copilot_immersive_task_within_chat_thread')
  }

  get previewFeaturesVisionGate() {
    return isFeatureEnabled('copilot_chat_vision_preview_gate')
  }

  get visionGAGate() {
    return isFeatureEnabled('copilot_chat_vision_dotcom_chat_ga_gate')
  }

  /**
   * Gates the per-model reasoning effort picker shown next to the model picker.
   * Shares the CMC backend flag so the UI and the data-plane plumbing roll out together.
   */
  get codingAgentReasoningEffort() {
    return isFeatureEnabled('copilot_mission_control_reasoning_effort')
  }

  get codingAgentThirdPartyModelUI() {
    return isFeatureEnabled('coding_agent_third_party_model_ui')
  }

  get codingAgentTBBQuotaBanner() {
    return isFeatureEnabled('coding_agent_tbb_quota_banner')
  }

  get codingAgentEffortSelector() {
    return isFeatureEnabled('coding_agent_effort_selector')
  }

  get codingAgentCreateTaskStrip() {
    return isFeatureEnabled('coding_agent_create_task_strip')
  }

  get codingAgentHideModelPickerIfOnlyAuto() {
    return isFeatureEnabled('copilot_swe_agent_hide_model_picker_if_only_auto')
  }

  get copilotImmersiveSuggestionPills() {
    return isFeatureEnabled('copilot_immersive_suggestion_pills')
  }

  get filePreviewKeepMounted() {
    return isFeatureEnabled('copilot_immersive_file_preview_keep_mounted')
  }

  get codeBlockTransitionWrap() {
    return isFeatureEnabled('copilot_immersive_code_block_transition_wrap')
  }

  get fileBlockTransitionOpen() {
    return isFeatureEnabled('copilot_immersive_file_block_transition_open')
  }

  get mermaidDiagrams() {
    return isFeatureEnabled('copilot_immersive_mermaid_diagrams')
  }

  get diffExplainConversationIntent() {
    return isFeatureEnabled('copilot_diff_explain_conversation_intent')
  }

  get diffReferenceContext() {
    return isFeatureEnabled('copilot_diff_reference_context')
  }

  get prChatEnhancements() {
    return isFeatureEnabled('copilot_pr_chat_enhancements')
  }

  get prChatThreadPersistence() {
    return isFeatureEnabled('copilot_pr_chat_thread_persistence')
  }

  get prettifyPastedCode() {
    return isFeatureEnabled('copilot_chat_prettify_pasted_code')
  }

  get upgradeFreezeEnabled() {
    return isFeatureEnabled('copilot_upgrade_freeze')
  }

  get copilotTokenBasedBilling() {
    return isFeatureEnabled('copilot_token_based_billing')
  }

  get ipmUbbIndividualBudgetBanner() {
    return isFeatureEnabled('ipm_ubb_individual_budget_banner')
  }

  get noImplicitContext() {
    return isFeatureEnabled('copilot_no_implicit_context')
  }

  get userCanUpgradePlanField() {
    return isFeatureEnabled('copilot_user_can_upgrade_plan_field')
  }

  get autoModePickerFreeEdu() {
    return isFeatureEnabled('copilot_chat_auto_mode_picker_free_edu')
  }

  get autoModePickerPaid() {
    return isFeatureEnabled('copilot_chat_auto_mode_picker_paid')
  }

  get modelPickerPromotions() {
    return isFeatureEnabled('copilot_chat_model_picker_promotions')
  }

  get modelsBrowserCache() {
    return isFeatureEnabled('copilot_chat_models_browser_cache')
  }

  get xcodeInstallPage() {
    return isFeatureEnabled('copilot_xcode_install_page')
  }

  get chatHeaderReorder() {
    return isFeatureEnabled('copilot_chat_header_reorder')
  }

  /**
   * When enabled, surfaces a dismissible nudge above the immersive composer when the user returns to
   * a stale (untouched for 12h+), long (10+ message) thread, suggesting they start a fresh chat for a
   * new topic to keep usage lower. Dismissing it suppresses the nudge for the whole user for one week.
   * @returns {boolean} Whether the feature is enabled.
   */
  get newTopicNudge() {
    return isFeatureEnabled('copilot_chat_new_topic_nudge')
  }
}

export const copilotFeatureFlags = new CopilotFeatureFlags()
export type {CopilotFeatureFlags}
